import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Card, Button, Alert, Badge, Spinner, Table, Modal } from "react-bootstrap";
import { Calendar2Check, ArrowClockwise, CheckCircle, XCircle, CreditCard, InfoCircle, Plus, Trash, PencilSquare } from 'react-bootstrap-icons';
import { useLocation } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import { getPurchasesBySchool, updatePurchasesBySchool, createPurchaseForSchool, deletePurchaseById, getTeachersBySchoolId } from "../services/APIClient";

const trialOptions = [
  { id: "c0692678-544c-402f-84b4-8d303aae91a5", name: "May Free Trial 2024" },
  { id: "5d3b7014-d45a-41a0-a8ca-edd09090bc6e", name: "September Free Trial 2024" },
  { id: "30b2cfa0-ed05-4b9d-a47e-be2c71df801f", name: "No Trial - Straight Purchase" },
  { id: "f90778e6-2b11-4847-a246-b839f199105f", name: "UK Pilot 24/25 - Microlink" },
  { id: "50a72924-e121-4599-8f65-8bf7557e9473", name: "2 Year Pilot" },
  { id: "1ed3aa96-d938-46a0-85bc-9fb98529c529", name: "ALPACA Free Trial" }
];

export function getCurrentPurchasingSchoolYear(date = new Date()) {
  const currentYear = date.getFullYear();
  const academicYearBoundary = new Date(Date.UTC(currentYear, 7, 15));
  const comparableDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const startYear = comparableDate >= academicYearBoundary ? currentYear : currentYear - 1;

  return `${startYear}-${String(startYear + 1).slice(-2)}`;
}

function generateSchoolYearOptions(date = new Date()) {
  const currentSchoolYear = getCurrentPurchasingSchoolYear(date);
  const currentStartYear = Number(currentSchoolYear.slice(0, 4));
  const schoolYears = [];
  for (let i = -1; i <= 4; i++) {
    const startYear = currentStartYear + i;
    const endYear = startYear + 1;
    schoolYears.push(`${startYear}-${endYear.toString().slice(-2)}`);
  }
  return schoolYears;
}

const schoolYearOptions = generateSchoolYearOptions();

export function getPurchaseFormDefaults(date = new Date()) {
  return {
    teacher_name: "",
    teacherid: "",
    trialid: "1ed3aa96-d938-46a0-85bc-9fb98529c529",
    gradelevel1_numbers_suggested: "",
    gradelevel2_numbers_suggested: "",
    gradelevel1_screeners: false,
    gradelevel2_screeners: false,
    gradelevel3_screeners: false,
    amount_charged: 0,
    payment_date: "",
    next_payment_date: "",
    status: "active",
    purchaser_name: "",
    school_year: getCurrentPurchasingSchoolYear(date),
    purchase_type: "initial"
  };
}

export function preparePurchaseData(formData, isNewPurchase) {
  const purchaseData = { ...formData };
  const amountCharged = purchaseData.amount_charged === "" ? 0 : Number(purchaseData.amount_charged);

  if (!Number.isFinite(amountCharged) || amountCharged < 0) {
    throw new Error("Amount charged must be zero or a positive number.");
  }
  purchaseData.amount_charged = amountCharged;

  if (purchaseData.payment_date) {
    purchaseData.payment_date = new Date(purchaseData.payment_date).toISOString();
  }
  if (purchaseData.next_payment_date) {
    purchaseData.next_payment_date = new Date(purchaseData.next_payment_date).toISOString();
  }
  if (purchaseData.gradelevel1_numbers_suggested) {
    purchaseData.gradelevel1_numbers_suggested = parseInt(purchaseData.gradelevel1_numbers_suggested, 10) || null;
  }
  if (purchaseData.gradelevel2_numbers_suggested) {
    purchaseData.gradelevel2_numbers_suggested = parseInt(purchaseData.gradelevel2_numbers_suggested, 10) || null;
  }

  if (isNewPurchase) {
    Object.keys(purchaseData).forEach(key => {
      if (purchaseData[key] === "" || purchaseData[key] === null) {
        delete purchaseData[key];
      }
    });
  }

  return purchaseData;
}

export function PurchaseForm({ formData, teachers, onFormChange, onTeacherSelection }) {
  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Select Teacher</Form.Label>
            <Form.Select
              value={formData.teacherid}
              onChange={(e) => onTeacherSelection(e.target.value)}
            >
              <option value="">Select a teacher...</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </Form.Select>
            {teachers.length === 0 && (
              <Form.Text className="text-muted">
                No teachers found for this school. You can enter manually below.
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Teacher Name (Manual Entry)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter teacher name"
              value={formData.teacher_name}
              onChange={(e) => onFormChange('teacher_name', e.target.value)}
            />
            <Form.Text className="text-muted">
              Teacher ID: {formData.teacherid || 'Not selected'}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Amount Charged</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={formData.amount_charged}
              onChange={(e) => onFormChange('amount_charged', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.payment_date}
              onChange={(e) => onFormChange('payment_date', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Next Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.next_payment_date}
              onChange={(e) => onFormChange('next_payment_date', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={formData.status}
              onChange={(e) => onFormChange('status', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Purchase Type</Form.Label>
            <Form.Select
              value={formData.purchase_type}
              onChange={(e) => onFormChange('purchase_type', e.target.value)}
            >
              <option value="initial">Initial</option>
              <option value="renewal">Renewal</option>
              <option value="historical_migration">Historical Migration</option>
              <option value="upgrade">Upgrade</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>School Year</Form.Label>
            <Form.Select
              value={formData.school_year}
              onChange={(e) => onFormChange('school_year', e.target.value)}
            >
              <option value="">Select school year...</option>
              {schoolYearOptions.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Trial</Form.Label>
            <Form.Select
              value={formData.trialid}
              onChange={(e) => onFormChange('trialid', e.target.value)}
            >
              {trialOptions.map(trial => (
                <option key={trial.id} value={trial.id}>
                  {trial.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Form.Check
            id="gradelevel1-screeners"
            className="mb-3"
            type="checkbox"
            label="Grade Level 1 Screeners"
            checked={formData.gradelevel1_screeners}
            onChange={(e) => onFormChange('gradelevel1_screeners', e.target.checked)}
          />
        </Col>
        <Col md={4}>
          <Form.Check
            id="gradelevel2-screeners"
            className="mb-3"
            type="checkbox"
            label="Grade Level 2 Screeners"
            checked={formData.gradelevel2_screeners}
            onChange={(e) => onFormChange('gradelevel2_screeners', e.target.checked)}
          />
        </Col>
        <Col md={4}>
          <Form.Check
            id="gradelevel3-screeners"
            className="mb-3"
            type="checkbox"
            label="Grade Level 3 Screeners"
            checked={formData.gradelevel3_screeners}
            onChange={(e) => onFormChange('gradelevel3_screeners', e.target.checked)}
          />
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Purchaser Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter purchaser name"
              value={formData.purchaser_name}
              onChange={(e) => onFormChange('purchaser_name', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Grade Level 1 Numbers</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter suggested numbers"
              value={formData.gradelevel1_numbers_suggested}
              onChange={(e) => onFormChange('gradelevel1_numbers_suggested', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Grade Level 2 Numbers</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter suggested numbers"
              value={formData.gradelevel2_numbers_suggested}
              onChange={(e) => onFormChange('gradelevel2_numbers_suggested', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );
}

function PurchaseManagement() {
  const location = useLocation();
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [name, setName] = useState("User");
  const [schoolid, setSchoolid] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  
  // For purchase data
  const [purchases, setPurchases] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Form data for modal
  const [formData, setFormData] = useState(getPurchaseFormDefaults);
  const [modalError, setModalError] = useState(null);

  // For date formatting
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate purchase summary
  const getPurchaseSummary = () => {
    if (!purchases || purchases.length === 0) return null;
    
    const totalAmount = purchases.reduce((sum, purchase) => sum + (parseFloat(purchase.amount_charged) || 0), 0);
    const schoolYears = [...new Set(purchases.map(p => p.school_year).filter(Boolean))].sort();
    const purchaseTypes = [...new Set(purchases.map(p => p.purchase_type).filter(Boolean))];
    const activePurchases = purchases.filter(p => p.status === 'active').length;
    const latestPurchase = purchases.reduce((latest, current) => {
      const currentDate = new Date(current.payment_date || current.created_date);
      const latestDate = new Date(latest.payment_date || latest.created_date);
      return currentDate > latestDate ? current : latest;
    }, purchases[0]);

    return {
      totalAmount,
      schoolYears,
      purchaseTypes,
      activePurchases,
      latestPurchase,
      totalPurchases: purchases.length
    };
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'cancelled': return 'danger';
      case 'expired': return 'warning';
      case 'pending': return 'primary';
      default: return 'secondary';
    }
  };

  // Get trial name by ID
  const getTrialNameById = (trialId) => {
    const trial = trialOptions.find(trial => trial.id === trialId);
    return trial ? trial.name : trialId || 'None';
  };

  // Reset form data
  const resetFormData = () => {
    setFormData(getPurchaseFormDefaults());
    setModalError(null);
  };

  // Handle device orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    setName("User");
    const params = new URLSearchParams(location.search);
    const schoolIdFromQuery = params.get('schoolId');
    const schoolNameFromQuery = params.get('schoolName');
    if (schoolIdFromQuery) {
      setSchoolid(schoolIdFromQuery);
      if (schoolNameFromQuery) setSchoolName(decodeURIComponent(schoolNameFromQuery));
    }
  }, []);

  // Fetch purchase data when school ID is set
  useEffect(() => {
    if (schoolid) {
      fetchPurchaseData();
      fetchTeacherData();
    }
  }, [schoolid]);

  // Fetch teacher data from API
  const fetchTeacherData = async () => {
    try {
      const response = await getTeachersBySchoolId(schoolid);
      if (response && response.length > 0) {
        setTeachers(response);
      } else {
        setTeachers([]);
      }
    } catch (err) {
      console.error("Error fetching teacher data:", err);
      setTeachers([]);
    }
  };

  // Fetch purchase data from API
  const fetchPurchaseData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await getPurchasesBySchool(schoolid, {
        order: ['payment_date DESC', 'created_date DESC']
      });
      
      if (response && response.length > 0) {
        setPurchases(response);
      } else {
        setPurchases([]);
      }
    } catch (err) {
      console.error("Error fetching purchase data:", err);
      setError(`Error fetching purchase data: ${err.response?.data?.error?.message || err.message}`);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle teacher selection
  const handleTeacherSelection = (teacherId) => {
    const selectedTeacher = teachers.find(teacher => teacher.id === teacherId);
    if (selectedTeacher) {
      setFormData(prev => ({
        ...prev,
        teacherid: selectedTeacher.id,
        teacher_name: selectedTeacher.name
      }));
    }
  };

  // Open edit modal
  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      teacher_name: purchase.teacher_name || "",
      teacherid: purchase.teacherid || "",
      trialid: purchase.trialid || "1ed3aa96-d938-46a0-85bc-9fb98529c529",
      gradelevel1_numbers_suggested: purchase.gradelevel1_numbers_suggested || "",
      gradelevel2_numbers_suggested: purchase.gradelevel2_numbers_suggested || "",
      gradelevel1_screeners: purchase.gradelevel1_screeners === true,
      gradelevel2_screeners: purchase.gradelevel2_screeners === true,
      gradelevel3_screeners: purchase.gradelevel3_screeners === true,
      amount_charged: purchase.amount_charged ?? 0,
      payment_date: formatDateForInput(purchase.payment_date),
      next_payment_date: formatDateForInput(purchase.next_payment_date),
      status: purchase.status || "active",
      purchaser_name: purchase.purchaser_name || "",
      school_year: purchase.school_year || "",
      purchase_type: purchase.purchase_type || "initial"
    });
    setModalError(null);
    setShowEditModal(true);
  };

  // Open add modal
  const handleAddPurchase = () => {
    resetFormData();
    setEditingPurchase(null);
    setShowAddModal(true);
  };

  // Close modals
  const handleCloseModals = () => {
    setShowEditModal(false);
    setShowAddModal(false);
    setEditingPurchase(null);
    resetFormData();
  };

  // Save purchase (add or edit)
  const handleSavePurchase = async () => {
    setModalLoading(true);
    setError(null);
    setSuccess(null);
    setModalError(null);
    
    try {
      const purchaseData = preparePurchaseData(formData, !editingPurchase);
      
      if (editingPurchase) {
        // Update existing purchase
        await updatePurchasesBySchool(schoolid, purchaseData, { id: editingPurchase.id });
        setSuccess("Successfully updated purchase record");
      } else {
        // Create new purchase
        await createPurchaseForSchool(schoolid, purchaseData);
        setSuccess("Successfully created new purchase record");
      }
      
      // Close modal and refresh data
      handleCloseModals();
      fetchPurchaseData();
    } catch (err) {
      console.error("Error saving purchase:", err);
      const message = `Error saving purchase: ${err.response?.data?.error?.message || err.message}`;
      setError(message);
      setModalError(message);
    } finally {
      setModalLoading(false);
    }
  };

  // Delete purchase
  const handleDeletePurchase = async (purchaseId) => {
    if (!window.confirm("Are you sure you want to delete this purchase record? This action cannot be undone.")) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await deletePurchaseById(purchaseId);
      setSuccess("Successfully deleted purchase record");
      fetchPurchaseData();
    } catch (err) {
      console.error("Error deleting purchase:", err);
      setError(`Error deleting purchase: ${err.response?.data?.error?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const summary = getPurchaseSummary();

  return (
    <div>
      <Container fluid className="vh-100">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} />

        <Row className="h-100">
          <LeftPanel />

          <Col sm={{ span: 10, offset: 2 }} className="blue-background d-flex flex-column justify-content-start align-items-center">
            
            {/* Page Header */}
            <div className="w-75 mt-4 mb-3">
              <h2 className="text-white">Purchase Management</h2>
              <p className="text-white">View and manage purchase records for schools</p>
            </div>
            
            {/* School Selection Section */}
            <div className="w-75 mt-2 bg-white rounded shadow-sm p-4">
              <h4 className="mb-3">School Selection</h4>
              <SchoolRollNumberInput
                onSchoolIdRetrieved={setSchoolid}
                schoolNameRetrieved={setSchoolName}
              />
              
              {schoolName && (
                <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
                  <div>
                    <strong className="me-2">Selected School:</strong> 
                    <span className="text-primary">{schoolName}</span>
                  </div>
                  {schoolid && (
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => fetchPurchaseData()}
                      disabled={loading}
                    >
                      <ArrowClockwise className="me-1" /> Refresh
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Loading spinner */}
            {loading && (
              <div className="w-75 mt-4 text-center">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="w-75 mt-4">
                <Alert variant="danger">
                  <strong>Error: </strong>{error}
                </Alert>
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="w-75 mt-4">
                <Alert variant="success">
                  <strong>Success: </strong>{success}
                </Alert>
              </div>
            )}

            {/* Purchase Summary */}
            {summary && (
              <div className="w-75 mt-4">
                <Card className="shadow">
                  <Card.Header className="bg-success text-white">
                    <h4 className="mb-0 d-flex align-items-center">
                      <CreditCard className="me-2" />
                      Purchase Summary
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <div className="text-center">
                          <h5 className="text-primary">{summary.totalPurchases}</h5>
                          <small className="text-muted">Total Purchases</small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h5 className="text-success">${summary.totalAmount.toFixed(2)}</h5>
                          <small className="text-muted">Total Amount</small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h5 className="text-info">{summary.activePurchases}</h5>
                          <small className="text-muted">Active Purchases</small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h5 className="text-warning">{formatDateForDisplay(summary.latestPurchase?.payment_date)}</h5>
                          <small className="text-muted">Latest Payment</small>
                        </div>
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={6}>
                        {summary.schoolYears.length > 0 && (
                          <div>
                            <small className="text-muted">School Years: </small>
                            {summary.schoolYears.map(year => (
                              <Badge key={year} bg="secondary" className="me-1">{year}</Badge>
                            ))}
                          </div>
                        )}
                      </Col>
                      <Col md={6}>
                        {summary.purchaseTypes.length > 0 && (
                          <div>
                            <small className="text-muted">Purchase Types: </small>
                            {summary.purchaseTypes.map(type => (
                              <Badge key={type} bg="info" className="me-1">{type}</Badge>
                            ))}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Purchase Records Table */}
            {purchases.length > 0 && (
              <div className="w-75 mt-4 mb-5">
                <Card className="shadow">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Purchase Records</h4>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleAddPurchase}
                      disabled={loading}
                    >
                      <Plus className="me-1" />
                      Add New Purchase
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Teacher</th>
                            <th>Amount</th>
                            <th>Payment Date</th>
                            <th>Next Payment</th>
                            <th>Status</th>
                            <th>School Year</th>
                            <th>Type</th>
                            <th>Purchaser</th>
                            <th>Screeners</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.map((purchase) => (
                            <tr key={purchase.id}>
                              <td>
                                <div>
                                  <strong>{purchase.teacher_name || 'Not specified'}</strong>
                                  <br />
                                  <small className="text-muted">ID: {purchase.teacherid || 'None'}</small>
                                </div>
                              </td>
                              <td>
                                <strong>${parseFloat(purchase.amount_charged || 0).toFixed(2)}</strong>
                              </td>
                              <td>{formatDateForDisplay(purchase.payment_date)}</td>
                              <td>{formatDateForDisplay(purchase.next_payment_date)}</td>
                              <td>
                                <Badge bg={getStatusVariant(purchase.status)}>
                                  {purchase.status || 'No Status'}
                                </Badge>
                              </td>
                              <td>{purchase.school_year || 'Not set'}</td>
                              <td>
                                <Badge bg="secondary">
                                  {purchase.purchase_type || 'Not set'}
                                </Badge>
                              </td>
                              <td>
                                <div>
                                  <strong>{purchase.purchaser_name || 'Not specified'}</strong>
                                  <br />
                                  <small className="text-muted">
                                    Grade 1: {purchase.gradelevel1_numbers_suggested || 'N/A'} |
                                    Grade 2: {purchase.gradelevel2_numbers_suggested || 'N/A'}
                                  </small>
                                  <br />
                                  <small className="text-muted">
                                    Trial: {getTrialNameById(purchase.trialid)}
                                  </small>
                                </div>
                              </td>
                              <td>
                                {[1, 2, 3].map(level => (
                                  <Badge
                                    key={level}
                                    bg={purchase[`gradelevel${level}_screeners`] === true ? "success" : "secondary"}
                                    className="me-1"
                                  >
                                    GL{level}
                                  </Badge>
                                ))}
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditPurchase(purchase)}
                                    disabled={loading}
                                    title="Edit this purchase"
                                  >
                                    <PencilSquare />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeletePurchase(purchase.id)}
                                    disabled={loading}
                                    title="Delete this purchase"
                                  >
                                    <Trash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* No purchases message */}
            {schoolid && !loading && purchases.length === 0 && (
              <div className="w-75 mt-4">
                <Card className="shadow text-center">
                  <Card.Body className="py-5">
                    <InfoCircle size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">No Purchase Records Found</h5>
                    <p className="text-muted">No purchase records exist for this school yet.</p>
                    <Button
                      variant="success"
                      onClick={handleAddPurchase}
                      disabled={loading}
                    >
                      <Plus className="me-2" />
                      Add First Purchase
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Edit Purchase Modal */}
      <Modal show={showEditModal} onHide={handleCloseModals} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Purchase Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <PurchaseForm
                formData={formData}
                teachers={teachers}
                onFormChange={handleFormChange}
                onTeacherSelection={handleTeacherSelection}
              />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals} disabled={modalLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSavePurchase} disabled={modalLoading}>
            {modalLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Update Purchase
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Purchase Modal */}
      <Modal show={showAddModal} onHide={handleCloseModals} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Purchase</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <PurchaseForm
                formData={formData}
                teachers={teachers}
                onFormChange={handleFormChange}
                onTeacherSelection={handleTeacherSelection}
              />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals} disabled={modalLoading}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSavePurchase} disabled={modalLoading}>
            {modalLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Add Purchase
          </Button>
        </Modal.Footer>
      </Modal>

      {/* OrientationPrompt for portrait mode */}
      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default PurchaseManagement;
